import { useMemo, useState } from "react"
import { observer } from "mobx-react"
import {
  Button,
  Col,
  Form,
  FormInstance,
  FormListFieldData,
  Input,
  Row,
  Select,
} from "antd"
import { DefaultOptionType } from "antd/es/select"
import { MinusCircleOutlined } from "@ant-design/icons"

import { DeleteConfirm } from "@src/app/components/DeleteConfirm"
import { notify } from "@src/app/components/notify"
import css from "../../index.module.css"

export type ManualPayoutsFormItemProps = {
  available?: number
  currencies: {
    isLoading: boolean
    options: DefaultOptionType[]
  }
  errors: ReturnType<FormInstance["getFieldsError"]>
  field: FormListFieldData
  onRemove: (i: number) => void
}
export const ManualPayoutsFormItem = observer(
  ({
    available,
    currencies,
    errors,
    field: { key: fieldKey, name: fieldName, ...field },
    onRemove,
  }: ManualPayoutsFormItemProps) => {
    const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false)
    const [paymentToDelete, setPaymentToDelete] = useState(0)
    const form = Form.useFormInstance()
    const showDeleteConfirm = (payment: number) => {
      setPaymentToDelete(payment)
      setOpenDeleteConfirm(true)
    }

    const onDeleteConfirm = () => {
      onRemove(paymentToDelete)
      notify.success("Payment has been deleted")
      setOpenDeleteConfirm(false)
    }

    const hasAddressErrors = useMemo(() => {
      if (!errors) {
        return false
      }

      return Boolean(
        errors.find(
          (i) =>
            JSON.stringify(i.name) ===
            JSON.stringify(["payouts", String(fieldName), "address"]),
        )?.errors.length,
      )
    }, [errors])

    return (
      <Row gutter={16} key={fieldKey}>
        <Col span={12}>
          <Form.Item
            {...field}
            className={css.addressItem}
            hasFeedback
            id="GENERATED_ID_MANUALPAYOUTSFORMITEM_TSX_FORM.ITEM_VOID_1"
            label="Address"
            name={[String(fieldName), "address"]}
            rules={[
              {
                message: "Please input network address",
                required: true,
              },
            ]}
          >
            <Input placeholder="Input address" />
          </Form.Item>
        </Col>
        <Col span={5}>
          <Form.Item
            {...field}
            dependencies={[["payouts", String(fieldName), "currency"]]}
            id="GENERATED_ID_MANUALPAYOUTSFORMITEM_TSX_FORM.ITEM_VOID_2"
            label="Amount"
            name={[String(fieldName), "amount"]}
            rules={[
              {
                required: true,
                validator: (_, value) => {
                  if (!value.match(/^(?!0{2,})\d+(\.(\d){0,}?)?$/)) {
                    return Promise.reject(
                      new Error("Invalid number of decimal places"),
                    )
                  }

                  return Promise.resolve()
                },
              },
              {
                max: available ?? Infinity,
                message: `Available: ${available}`,
                transform: (v) => Number.parseFloat(v),
                type: "float",
                validator: ({ max, message }, value) => {
                  if (max && max <= value) {
                    return Promise.reject(message)
                  }

                  return Promise.resolve()
                },
              },
            ]}
          >
            <Input placeholder="0.00" />
          </Form.Item>
        </Col>
        <Col span={5}>
          <Form.Item
            {...field}
            id="GENERATED_ID_MANUALPAYOUTSFORMITEM_TSX_FORM.ITEM_VOID_3"
            label="Currency"
            name={[String(fieldName), "currency"]}
            rules={[
              {
                message: "Please select currency",
                required: true,
              },
            ]}
          >
            <Select
              loading={currencies.isLoading}
              options={currencies.options}
              placeholder="Select currency"
            />
          </Form.Item>
        </Col>
        <Col span={1} style={{ marginTop: 30 }}>
          <Button
            icon={<MinusCircleOutlined />}
            id="GENERATED_ID_MANUALPAYOUTSFORMITEM_BUTTON_VOID_1"
            onClick={() => showDeleteConfirm(fieldName)}
            size={"small"}
            style={{ padding: 0 }}
            type={"text"}
          ></Button>
        </Col>

        <DeleteConfirm
          cancelText="Cancel"
          isLoading={currencies.isLoading}
          okText="Delete Payout"
          onCancel={() => setOpenDeleteConfirm(false)}
          onOk={() => onDeleteConfirm()}
          open={openDeleteConfirm}
          text="This action cannot be undone"
          title="Are you sure you want to delete this payout?"
        />
      </Row>
    )
  },
)
